import { css } from '@emotion/css';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../features';
import { actions } from '../../../features/dream';
import { sendEvent } from '../../../lib/google-analytics';
import { ReportCategory } from '../../../models/ReportCategory';
import { BackButton } from '../../shared/buttons/BackButton';
import { ReportForm } from '../../shared/report/ReportForm';
import { DialogRoute } from '../../shared/routes/DialogRoute';
import { DreamHistoryReportFailedDialog } from './DreamHistoryReportFailedDialog';

export const DreamHistoryReportForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ address?: string }>();
  const { t, i18n } = useTranslation(['best_friends', 'dream']);
  const { historyDetail } = useSelector((state: RootState) => state.dream);
  const dispatch = useDispatch();

  const address = params?.address;

  useEffect(() => {
    return () => {
      dispatch(actions.resetReportUser());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!address) {
      return;
    }
    switch (historyDetail.reportStatus?.state) {
      case 'ok':
        history.replace(`/players/dream/history/report-completed/${address}`);
        break;
      case 'error':
        history.push(`/players/dream/history/report/${address}/failed`);
        break;
    }
  }, [address, historyDetail.reportStatus, history]);

  const handleSubmit = (category: ReportCategory, reason: string) => {
    if (historyDetail.userProfile?.state === 'ok') {
      const ugc = historyDetail.userProfile.result;
      const language = i18n.language;
      const type = 'user_profile';
      dispatch(actions.reportUser({ type, category, language, reason, ugc }));
    }
    sendEvent('best_friends_report', 'click');
  };

  if (!historyDetail) {
    return <Redirect to="/players/dream/history" />;
  }

  return (
    <>
      <div
        className={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100vh;
          background: #f5f1e3;
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 20;
          * {
            font-family: system-ui, -apple-system, sans-serif;
            font-weight: bold;
          }
          header {
            flex: 0 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 52px;
            padding: 0 4px;

            .space {
              width: 48px;
              height: 100%;
            }
          }
          h1 {
            font-size: 16px;
            color: #4d3906;
          }
          .body {
            flex: 1 1 auto;
            width: 100%;
            overflow-y: scroll;
          }
        `}
      >
        <header>
          <BackButton ariaLabel={t('dream:aria_button_back_to_dream_history')} />
          <h1>{t('report_spam')}</h1>
          <div className="space" />
        </header>
        <div className="body">
          <ReportForm
            onSubmit={handleSubmit}
            isLoading={historyDetail.reportStatus?.state === 'pending'}
          />
        </div>
      </div>
      <DialogRoute path="/players/dream/history/report/:address/failed">
        <DreamHistoryReportFailedDialog />
      </DialogRoute>
    </>
  );
};
